import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/styles";
import { Checkbox, Divider, FormControlLabel, Snackbar } from "@material-ui/core";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import { Alert } from "@material-ui/lab";

import uiConf from "../../config/uiConf";

import apiClient from "../../lib/common/apiClient";
import checkValidation from "../../lib/utils/checkValidation";
import checkConditionWithMsg from "../../lib/utils/checkConditionWithMsg";

import Dialog from "../dialog/functional/Dialog";
import DialogHeader from "../dialog/functional/DialogHeader";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";

import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";

import AutoForm from "../form/functional/AutoForm";

import getSchema from "../../lib/api/getSchema";

import { debounce } from "lodash";
import defineConf from "../../config/defineConf";

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px",
    },

    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text,
    },
}));

function TkInputInspectionCreateDialog(props) {
    const classes = useStyles();

    const {
        id,
        mode,
        schema,
        refresh,

        onSelectMst,

        title,
        formId,

        moveable,
        resizable,
        fullWidth,

        onInitialized,
        onSuccess,
        onClose,

        onDialogUpdateDetail,

        appendCheckBox,

        ...others
    } = props;

    const [dialogInstance, setDialogInstance] = useState();
    const [okButtonDisabled, setOkButtonDisabled] = useState(true);
    const [formData, setFormData] = useState({});
    const [keepOpen, setKeepOpen] = useState(false);

    const [height, setHeight] = useState(0);

    const [schemaData, setSchemaData] = useState();
    const [grid, setGrid] = useState();
    const [api, setApi] = useState();
    const [onSelectDialog, setOnSelectDialog] = useState();

    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    const [isEmpty, setIsEmpty] = useState(false);

    const onLoadData = (data) => {
        setIsEmpty(data.length < 1 ? true : false);

        if (data.length < 1) {
            setOkButtonDisabled(true);
        }
    };

    const handleResize = () => {
        setHeight(document.documentElement.offsetHeight - 96);
    };

    useEffect(() => {
        window.addEventListener("resize", handleResize);

        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        let mounted = true;

        (async () => {
            let schema = await getSchema("TkVwInputInspection");
            if (mounted) {
                setSchemaData(schema);
            }
        })();

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        (async () => {
            if (grid) {
                grid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setOnSelectDialog(grid.selectedItems[0]);
                        }
                    }),
                    10
                );

                grid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setOnSelectDialog(grid.selectedItems[0]);
                        }
                    }),
                    10
                );
            }
        })();
    }, [grid]);

    useEffect(() => {
        if (onSelectDialog) {
            setFormData((formData) => {
                console.log(onSelectDialog);
                let newFormData = {
                    ...formData,
                    orderPurchaseId: onSelectDialog?.orderPurchaseId,
                    orderPurchaseDetailId: onSelectDialog?.orderPurchaseDetailId,
                    orderBuyerDetailId: onSelectDialog?.orderBuyerDetailId,
                    inputInspectionContainerId: onSelectMst?.inputInspectionContainerId,
                    workOrderNumber: onSelectDialog?.workOrderNumber,
                    modelName: onSelectDialog?.modelName,
                    modelPartName: onSelectDialog?.modelPartName,
                    materialCode: onSelectDialog?.materialCode,
                    materialCodeVer: onSelectDialog?.materialCodeVer,
                    opCode: onSelectDialog?.opCode,
                    partNo: onSelectDialog?.partNo,
                    partSeq: onSelectDialog?.partSeq,
                    cutCode2: onSelectDialog?.cutCode2,
                    abbreviations: onSelectDialog?.abbreviations,
                    material: onSelectDialog?.material,
                    itemSpecName: onSelectDialog?.itemSpecName,
                    t_size: onSelectDialog?.t_size,
                    w_size: onSelectDialog?.w_size,
                    l_size: onSelectDialog?.l_size,
                    t_delta: onSelectDialog?.t_delta,
                    w_delta: onSelectDialog?.w_delta,
                    l_delta: onSelectDialog?.l_delta,
                    inQuantity: onSelectDialog?.qty,
                    qcResult: defineConf.qcResult.QC_OK,
                    defectiveCode:
                        !formData?.qcResult || formData?.qcResult === defineConf.qcResult.QC_OK
                            ? "INS000"
                            : formData?.defectiveCode,
                };

                setOkButtonDisabled(false);

                return newFormData;
            });
        }
    }, [onSelectDialog, onSelectMst]);

    useEffect(() => {
        if (onSelectMst?.inputInspectionContainerId) {
            setFormData((formData) => {
                let newFormData = {
                    ...formData,
                    inputInspectionContainerId: onSelectMst?.inputInspectionContainerId,
                };

                return newFormData;
            });
        }
    }, [onSelectMst?.inputInspectionContainerId]);

    useEffect(() => {
        if (isEmpty) {
            setFormData({});
        }
    }, [isEmpty]);

    const { t } = useTranslation();

    const onCloseDialog = () => {
        setOkButtonDisabled(true);

        if (onClose) {
            onClose();
        }
    };

    const onCancel = () => {
        setOkButtonDisabled(true);

        dialogInstance.hide();
    };

    const onDialogInitialized = (instance) => {
        setDialogInstance(instance);

        if (onInitialized) {
            onInitialized(instance);
        }
    };

    const onGridInitialized = (grid) => {
        grid.selectionMode = "Row";

        setGrid(grid);
    };

    const onApiInitialized = (api) => {
        setApi(api);
    };

    const onChange = (formData) => {
        setFormData(formData);

        setOkButtonDisabled(false);
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const refreshed = (msg) => {
        notifySuccess(msg);

        if (api) {
            api.refresh();
        }

        if (refresh) {
            refresh.refresh();
        }
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const nofifyWarning = (msg) => {
        console.warn(msg);

        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onApiSuccess = (result) => {
        refreshed();

        if (onSuccess) {
            setTimeout(onSuccess(result.data), 0);
        }
        if (!keepOpen) {
            dialogInstance.hide();
        }

        setFormData({ inputInspectionContainerId: onSelectMst?.inputInspectionContainerId });
    };

    const onApiFailure = (reason) => {
        notifyFailure(
            t("error.insertFailure", {
                reason: reason.response ? reason.response.data : JSON.stringify(reason),
            })
        );
    };

    const checkItemValidation = (schema, formData) => {
        for (const column of schema.columns) {
            let [valid, msg] = checkValidation("create", column, column.validation, formData[column.name] || "");

            if (!valid) {
                nofifyWarning(msg);
                return false;
            }
        }

        return true;
    };

    const onOk = (schema, formData) => {
        console.log(formData);
        if (formData.qcResult === defineConf.qcResult.QC_OK) {
            formData.defectiveCode = "INS000";
        }

        if (!checkItemValidation(schema, formData)) {
            console.error(" From Validation Failed ");

            return;
        }

        if (schema.validation) {
            let [valid, msg] = checkConditionWithMsg(formData, schema.validation);

            if (!valid) {
                nofifyWarning(t("warning.formDataValidationFail", { msg: msg }));
                return;
            }
        }

        let url = `/api/TkInputInspection`;

        let apiData = { ...formData };

        let form = new FormData();

        let json = {};
        let deleted = {};

        for (const formItem in apiData) {
            if (apiData[formItem] !== undefined) {
                json[formItem] = apiData[formItem];
            }
        }
        console.log(apiData);
        form.append("deleted", JSON.stringify(deleted));
        form.append("json", JSON.stringify(json));
        console.log(form.json);
        apiClient
            .post(url, form, {
                headers: {
                    "Content-Type": "application/json",
                },
            })
            .then((result) => onApiSuccess(result))
            .catch((reason) => onApiFailure(reason));
    };

    const onCloseNotification = () => {
        setNotification((notification) => {
            return { ...notification, open: false };
        });
    };

    const checkKeepOpen = () => {
        return (
            <>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => !keepOpen)}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />
                <div style={{ flexGrow: 1 }}></div>
            </>
        );
    };

    const onModifyViewUrl = (url) => {
        if (onSelectMst?.orderPurchaseId) {
            url += "/" + onSelectMst?.orderPurchaseId;
        }

        return url;
    };

    return (
        <>
            <Dialog
                id={id}
                moveable={moveable ? moveable : uiConf.dialogMoveable}
                resizable={resizable ? resizable : uiConf.dialogResizable}
                fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
                maxWidth="lg"
                onClose={onCloseDialog}
                onInitialized={onDialogInitialized}
                {...others}
            >
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>

                <Divider />

                <DialogContent style={{ height: height }}>
                    <ReflexContainer orientation="vertical">
                        <ReflexElement>
                            <BasicCrudScreenType
                                id={formId}
                                embededInOtherScreenType
                                headerTitle={t("term.poDetail")}
                                table="MomSysZvwOrderPurchaseDetailNotInput"
                                view="MomSysZvwOrderPurchaseDetailNotInput"
                                noCreate
                                noEdit
                                noDelete
                                noExcel
                                noOutputSize
                                noFilter
                                onApiInitialized={onApiInitialized}
                                onInitialized={onGridInitialized}
                                onModifyViewUrl={onModifyViewUrl}
                                onLoadData={onLoadData}
                            />
                        </ReflexElement>
                        <ReflexSplitter />
                        <ReflexElement>
                            <div
                                style={{
                                    padding: "32px",
                                }}
                            >
                                <AutoForm
                                    id="TkInputInspection"
                                    schema={schemaData}
                                    mode="edit"
                                    initialData={formData}
                                    onChange={onChange}
                                    modifyFormDataCallback={(formData) => {
                                        if (formData.qcResult === defineConf.qcResult.QC_OK) {
                                            formData.defectiveCode = "INS000";
                                        }
                                    }}
                                />
                            </div>
                        </ReflexElement>
                    </ReflexContainer>

                    <Snackbar
                        open={notification.open && notification.severity !== "success"}
                        autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                        onClose={onCloseNotification}
                    >
                        <Alert onClose={onCloseNotification} variant="filled" severity={notification.severity}>
                            {notification.msg}
                        </Alert>
                    </Snackbar>
                </DialogContent>

                <Divider />

                <DialogActionsOkCancel
                    firstActionComponent={checkKeepOpen}
                    labels={[t("common.add")]}
                    buttonDisabled={[okButtonDisabled, false]}
                    onOk={() => onOk(schema, formData)}
                    onCancel={onCancel}
                ></DialogActionsOkCancel>
            </Dialog>
        </>
    );
}

export default TkInputInspectionCreateDialog;
